import { Directive, ElementRef, HostListener, Optional, inject } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

/**
 * Makes the ng-select clear (X) button actually clear in this wizard.
 *
 * Why this is needed: ng-select v15's single-select renders its search
 * `<input>` (`.ng-input`) absolutely positioned with `width: 100%`, so it
 * overlays the whole control — including the X. A click on the X therefore has
 * the `<input>` as its event target, not `.ng-clear-wrapper`. ng-select's own
 * `handleMousedown` only clears when the target IS `.ng-clear-wrapper`, so the
 * X silently does nothing (while Backspace, which goes through the input, still
 * clears). See the console finding: `target=INPUT | insideClear=false`.
 *
 * This directive sidesteps the layering entirely: on any click inside the
 * control it checks whether the click COORDINATES fall within the
 * `.ng-clear-wrapper` bounding box and, if so, calls the ng-select instance's
 * own `handleClearClick()`. That runs ng-select's full clear path — nulls the
 * model, repaints the value container, and emits (clear)/(change) — so every
 * dropdown's existing clear wiring keeps working, with no per-control handler.
 *
 * Applied automatically to every `.ss-select` in the wizard via its selector.
 */
@Directive({
  selector: 'ng-select.ss-select',
  standalone: true,
})
export class SsSelectClearDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  // The ng-select instance this directive sits on. Optional guards against the
  // selector ever matching a non-ng-select element.
  constructor(@Optional() private readonly ngSelect: NgSelectComponent | null) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.ngSelect) {
      return;
    }
    const clearEl = this.host.nativeElement.querySelector(
      '.ng-clear-wrapper',
    ) as HTMLElement | null;
    if (!clearEl) {
      return;
    }
    const rect = clearEl.getBoundingClientRect();
    const insideClear =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (insideClear) {
      event.stopPropagation();
      this.ngSelect.handleClearClick();
    }
  }
}
